import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Auth } from 'auxilium-connect';
import { interval, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LanguageDef } from 'src/lang';
import {
  ConfirmDialogComponent,
  ConfirmDialogParams
} from '../components/confirm-dialog/confirm-dialog.component';
import { DebugComponent } from '../components/debug/debug.component';
import {
  ReorderDialogComponent,
  ReorderDialogParams
} from '../components/reorder-dialog/confirm-dialog.component';
import {
  ScannerConfirmDialogComponent,
  ScannerConfirmDialogParams
} from '../components/scanner-confirm-dialog/scanner-confirm-dialog.component';
import { Customer } from '../models/customer';
import { Ingredient } from '../models/ingredient';
import { Production } from '../models/production';
import { ProductionItems } from '../models/productionitems';
import { Recipe } from '../models/recipe';
import { TranslatePipe } from '../pipes/translate.pipe';
import { sameDate } from '../utils/sameDate';
import { ApiService } from './api.service';
import { NotifyService } from './notify.service';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  auth: Observable<Auth>;
  token;
  slices = {
    customers: 51483,
    recipes: 51468,
    recipeitems: 51608,
    production: 51693,
    productionitems: 51703,
    inventorytracker: 51583,
    invoices: 51493,
    invoiceitems: 51503,
    inventory: 51423,
    retailinventory: 51673,
    retailinventorytracker: 51688
  };
  private _languages: { id: string; label: string }[];

  insertProduction(product) {
    return this._api.insertRow(this.slices.production, product);
  }

  updateProduction(product) {
    return this._api.updateRow(this.slices.production, product);
  }

  insertProductionItems(productionItems) {
    return this._api.insertRows(this.slices.productionitems, productionItems);
  }

  updateProductionItems(productionItems) {
    return this._api.updateRows(this.slices.productionitems, productionItems);
  }

  insertInventoryTrackerItems(productionItems) {
    return this._api.insertRows(this.slices.inventorytracker, productionItems);
  }

  insertInvoice(invoice) {
    return this._api.insertRow(this.slices.invoices, invoice);
  }

  insertInvoiceItems(invoiceItems) {
    return this._api.insertRows(this.slices.invoiceitems, invoiceItems);
  }

  // removeProductionItems (productionItems){
  // 	return this._api.deleteRows(this.slices.productionitems, productionItems)
  // }

  removeProductionItems(id) {
    return this._api.request({
      '$/slice/xdelete': {
        slice: this.slices.productionitems,
        pkeys: [id]
      }
    });
  }

  version = environment.version;

  // a convenience observable for binding a live updating clock
  // of the current date/time
  now = interval(1000).pipe(
    map(() => new Date()),
    startWith(new Date())
  );

  // this will start with the current date/time, and update
  // when the DATE changes (eg, at midnight)
  today = this.now.pipe(
    startWith(new Date()),
    distinctUntilChanged((x, y) => sameDate(x, y))
  );

  constructor(
    public title: Title,
    private _api: ApiService,
    private _dialog: MatDialog,
    private _translate: TranslatePipe,
    public notify: NotifyService
  ) {
    this.auth = _api.auth;
  }

  translate(text: string, obj?: any) {
    return this._translate.transform(text, obj);
  }

  changeLanguage(to: keyof LanguageDef) {
    return this._translate.changeLanguage(to);
  }

  get language() {
    return this._translate.language;
  }

  get languages() {
    return (
      this._languages || (this._languages = this._translate.getAsOptions())
    );
  }

  logout() {
    return this._api.logout();
  }

  showDebug() {
    this._dialog.open(DebugComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      width: '95vw',
      height: '95vh'
    });
  }

  showLogin() {
    return this._api.showLogin();
  }

  confirm(data: ConfirmDialogParams, config?: MatDialogConfig) {
    return this._dialog
      .open(ConfirmDialogComponent, Object.assign({}, config, { data }))
      .afterClosed()
      .pipe(map((r) => !!r));
  }

  scanner_confirm(data: ScannerConfirmDialogParams, config?: MatDialogConfig) {
    return this._dialog
      .open(ScannerConfirmDialogComponent, Object.assign({}, config, { data }))
      .afterClosed()
      .pipe(map((r) => !!r));
  }

  reorder(data: ReorderDialogParams, config?: MatDialogConfig) {
    return this._dialog
      .open(ReorderDialogComponent, Object.assign({}, config, { data }))
      .afterClosed()
      .pipe(map((r) => !!r));
  }

  getRecipeItems(receipeRef) {
    const fields = [
      'id',
      'Cost',
      'IngredientstRef',
      'RecipeItemRef',
      'UnitMeasure',
      'Units',
      'UnitsProduced',
      'Done',
      {
        Ingredient: 'Ingredients:Item',
        InventoryQty: 'Ingredients:InventoryQty',
        Measurement: 'Ingredients:Measurment',
        Donottrack: 'Ingredients:DonotTrack_0'
      }
    ];
    let whereParams = ['$eq', ['$field', 'RecipeItemRef'], receipeRef];

    return this._api.request({
      '$/slice/report': {
        slice: this.slices.recipeitems,
        where: whereParams,
        fields
      },
      $pop: 'rows'
    });
  }

  getAllRecipes() {
    return this._api.request({
      '$/slice/report': {
        slice: this.slices.recipes
      },
      $pop: 'rows'
    });
  }

  getAllRecipes2(): Observable<Recipe[]> {
    return this._api.request<Recipe[]>({
      '$/slice/report': {
        slice: this.slices.recipes
      },
      $pop: 'rows'
    });
  }

  getAllIngredients(): Observable<Ingredient[]> {
    return this._api.request<Ingredient[]>({
      '$/slice/report': {
        slice: this.slices.inventory
      },
      $pop: 'rows'
    });
  }

  getAllTrackedItems(prodId): Observable<Ingredient[]> {
    let whereParams = ['$eq', ['$field', 'ProductionIDRef'], prodId];
    return this._api.request<Ingredient[]>({
      '$/slice/report': {
        slice: this.slices.inventorytracker,
        where: whereParams
      },
      $pop: 'rows'
    });
  }

  getAllProductions(): Observable<Production[]> {
    const fields = [
      'id',
      'RecipeRef',
      'OrderStatus',
      'ProductionDate',
      'ProductionQty',
      'RecipeRef',
      'Date',
      'CustomerRef',
      'SortOrder',
      'InvoiceCreated_0',
      'InventoryAdjusted_0',
      {
        Customer: 'Customer:Lookup',
        Product: 'Recipe:Title'
      }
    ];
    return this._api.request<Production[]>({
      '$/slice/report': {
        slice: this.slices.production,
        fields
      },
      $pop: 'rows'
    });
  }

  getProductionItems(prodref): Observable<ProductionItems[]> {
    const fields = [
      'id',
      'Item',
      'ProductionIdRef',
      'Price',
      'Qty',
      'Done_0',
      'InventoryItemRef',
      {
        Ingredient: 'Item',
        InventoryQty: 'Inventory Item:InventoryQty',
        Measurement: 'Inventory Item:Measurment',
        MeasurementDispensed: 'Inventory Item:MeasurmentDispensed',
        Donottrack: 'Inventory Item:DonotTrack_0',
        UnitsProduced: 'Production Id:ProductionQty',
        Value: 'Inventory Item:Value',
        Cost: 'Price',
        Units: 'Qty'
      }
    ];
    let whereParams = ['$eq', ['$field', 'ProductionIdRef'], prodref];
    return this._api.request<ProductionItems[]>({
      '$/slice/report': {
        slice: this.slices.productionitems,
        where: whereParams,
        fields
      },
      $pop: 'rows'
    });
  }

  getAllCustomers(): Observable<Customer[]> {
    return this._api.request<Customer[]>({
      '$/slice/report': {
        slice: this.slices.customers
      },
      $pop: 'rows'
    });
  }

  getRetailInventoryItem(id): Observable<Ingredient[]> {
    return this._api.request<Ingredient[]>({
      '$/slice/report': {
        slice: this.slices.retailinventory
      },
      $pop: 'rows'
    });
  }

  getRetailInventoryItemlike(id): Observable<Ingredient[]> {
    let whereParams = ['$like', ['$field', 'Lookup'], '%Miasdasdasdnt%'];
    return this._api.request<Ingredient[]>({
      '$/slice/report': {
        slice: this.slices.retailinventory,
        where: whereParams
      },
      $pop: 'rows'
    });
  }

  //BASKETIDRef

  getRetailInventoryItemTrackers(BASKETIDRef): Observable<Ingredient[]> {
    const fields = [
      'id',
      'Cost',
      'AddToInventory',
      'Amount',
      'InventoryItemRef',
      'Notes',
      'RemoveFromInventory',
      'Done',
      'BasketQty',
      {
        Item: 'Inventory Item:Item',
        ItemDescription: 'Inventory Item:ItemDescription',
        Basket_0: 'Inventory Item:Basket_0',
        Retail: 'Inventory Item:Retail',
        Quantity: 'BasketQty'
      }
    ];
    //["$nis",["$field","Lookup"],null]
    let whereParams = ['$eq', ['$field', 'BASKETIDRef'], BASKETIDRef];
    return this._api.request<Ingredient[]>({
      '$/slice/report': {
        slice: this.slices.retailinventorytracker,
        where: whereParams,
        fields
      },
      $pop: 'rows'
    });
  }

  addItemToInventory(item) {
    return this._api.insertRow(this.slices.retailinventory, item);
  }

  addItemToInventoryTracker(item) {
    return this._api.insertRow(this.slices.retailinventorytracker, item);
  }

  updateQuantity(retailIem) {
    return this._api.insertRow(this.slices.retailinventorytracker, retailIem);
  }

  updateQuantityMultiple(retailIem) {
    return this._api.insertRows(this.slices.retailinventorytracker, retailIem);
  }

  getMetaInfoRetail() {
    return this._api.request({
      '$/slice/fetch': {
        slice: this.slices.retailinventory
      }
    });
  }

  removeInventortrackerItemsBasket(id) {
    return this._api.request({
      '$/slice/xdelete': {
        slice: this.slices.retailinventorytracker,
        pkeys: id
      }
    });
  }

  removeBasket(id) {
    return this._api.request({
      '$/slice/xdelete': {
        slice: this.slices.retailinventory,
        pkeys: [id]
      }
    });
  }

  addAndUploadImgAppointment(imageUpload, retailItem) {
    console.log(retailItem);
    return this._api.uploadAndInsertRow(
      imageUpload,
      retailItem,
      this.slices.retailinventory,
      'Image'
    );
  }
}
